/**
 * What the visitor has agreed to, and nothing else.
 *
 * Analytics and advertising scripts set cookies and read them back, and in the EU that needs the
 * visitor's agreement first — given freely, per purpose, and as easy to take back as it was to
 * give (Directive 2002/58/EC art. 5(3), and чл. 4а ЗЕТ, read with the GDPR's standard of consent).
 * So nothing of the kind is loaded until somebody has said yes here, "no" is one click like "yes",
 * and the footer reopens the question at any time.
 *
 * The record of the choice is itself kept in localStorage without asking. That is the one kind of
 * storage the rule exempts: it is strictly necessary to honour the choice the visitor just made.
 */

export interface Consent {
  /** Measuring how the public site is used: Google Analytics. */
  analytics: boolean;
  /** Measuring and targeting advertising: Google Ads. */
  marketing: boolean;
  /** ISO instant of the decision — what "when did they agree" is answered from. */
  decidedAt: string;
  /** The version of the question they answered. Bumped when the purposes change, which asks again. */
  version: number;
}

/** Bump when a purpose is added or changes meaning. An older answer is then no answer. */
export const CONSENT_VERSION = 1;

/** A year, after which the question is put again. Regulators' guidance clusters around 6–13 months. */
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

const STORAGE_KEY = 'danak.consent';

type Listener = (consent: Consent | null) => void;

const listeners = new Set<Listener>();

/** The current, still-valid decision — or null when there is none and the banner has to ask. */
export function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as Partial<Consent>;

    if (
      stored.version !== CONSENT_VERSION
      || typeof stored.analytics !== 'boolean'
      || typeof stored.marketing !== 'boolean'
      || typeof stored.decidedAt !== 'string'
      // NaN compares false with everything, so an unparseable date has to be refused by name —
      // otherwise "soon" reads as a decision that never expires.
      || Number.isNaN(Date.parse(stored.decidedAt))
      || Date.now() - Date.parse(stored.decidedAt) > MAX_AGE_MS
    ) {
      return null;
    }

    return stored as Consent;
  } catch {
    // Private mode, or a value somebody edited. Either way: not a decision.
    return null;
  }
}

export function saveConsent(choice: { analytics: boolean; marketing: boolean }): Consent {
  const consent: Consent = { ...choice, decidedAt: new Date().toISOString(), version: CONSENT_VERSION };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Storage refused. The choice still holds for this page; it will simply be asked again.
  }

  listeners.forEach((listener) => listener(consent));
  return consent;
}

export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The banner is opened from two places that share no parent — it opens itself when there is no
 * decision, and the footer's "Cookie settings" link reopens it — so the request travels as an event.
 */
const OPEN_EVENT = 'danak:open-consent';

export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenConsentSettings(listener: () => void): () => void {
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
}
